import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Col, Row } from 'antd';
import dayjs from 'dayjs';
import { Container } from '@/components/Elements';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import { currencies } from '@/utils/currenciesUtils';
import {
  DatePickerField,
  FormLabel,
  InputField,
  RadioGroupField,
  SelectField,
} from '@/components/Form';

import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import {
  personalDetailLabelMapper,
  personalDetailSchema,
} from '../schema/personalDetailSchema';
import {
  AGREE_OPTIONS,
  CITIZENSHIP_OPTIONS,
  DEPENDENT_CHILDREN_OPTIONS,
  FREQUENCY_OPTIONS,
  MARITIAL_STATUS_OPTIONS,
  PRIVATE_SCHOOL_OPTIONS,
  RESIDENTIAL_STATUS_OPTIONS,
} from '@/constant/options';
import useScrollToTop from '@/hooks/useScrollTop';
import { usePostPurchaseRequestMutation } from '@/pages/mortgage/apis/purchaseAPI';
import { useFetchUserProfileQuery } from '@/store/apis/userApi';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';
import FormSubLabel from '@/components/Elements/Heading/FormSubLabel';

interface Props {
  data: any;
  setValue: any;
  watch: any;
  control: any;
  errors: any;
  unregister: any;
  getValues: any;
  resetField: any;
}

const NewPersonalDetail: React.FC<Props> = ({
  data: personalDetailData,
  setValue,
  watch,
  control,
  errors,
  unregister,
  getValues,
  resetField,
}) => {
  const { id } = useParams();
  useUnsavedChangesWarning(true);
  useScrollToTop();
  const countryOptions = useCountryOptions();
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const total = 5;

  const {
    data: userProfileData,
    error: userProfileDataError,
    isLoading: userProfileDataLoading,
  } = useFetchUserProfileQuery(undefined, {
    skip: false,
  });

  useEffect(() => {
    if (personalDetailData) {
      setValue('citizenship', personalDetailData?.citizenship);
      setValue('visaSubClass', personalDetailData?.visaSubClass);
      setValue('visaExpiryDate', personalDetailData?.visaExpiryDate);
      setValue('country', personalDetailData?.country);
      setValue('permanentResident', personalDetailData?.permanentResident);
      setValue('privateSchool', personalDetailData?.privateSchool);
      setValue(
        'currentResidentalStatus',
        personalDetailData?.currentResidentalStatus,
      );
      setValue(
        'residentalOtherSpecification',
        personalDetailData?.residentalOtherSpecification,
      );
      setValue('maritalStatus', personalDetailData?.maritalStatus);
      setValue('kidsAge', personalDetailData?.kidsAge);
      setValue('numberOfDependents', personalDetailData?.numberOfDependents);
      if (personalDetailData.currentResidentalStatus === 'renting') {
        setValue('rentExpense', personalDetailData.rentExpense ?? 0);
        setValue('frequencyOfRent', personalDetailData.frequencyOfRent ?? '');
      }
      if (personalDetailData.currentResidentalStatus === 'live_own_place') {
        setValue(
          'isTheResidentialPlaceMortgaged',
          personalDetailData.isTheResidentialPlaceMortgaged ?? null,
        );
      }
    } else if (userProfileData) {
      const userDetail = userProfileData.data.common_data;
      setValue('citizenship', userDetail.citizenship ?? null);
      setValue('country', userDetail.country ?? null);
      setValue('maritalStatus', userDetail.maritalStatus ?? null);
      setValue('permanentResident', userDetail.permanentResident ?? null);
    }
  }, [personalDetailData, userProfileData]);

  const watchCitizenship = watch('citizenship');
  const watchCurrentResidentalStatus = watch('currentResidentalStatus');
  const watchMaritalStatus = watch('maritalStatus');
  const watchDependentChildren = watch('dependentChildren');
  const numberOfDependents = watch('numberOfDependents');
  const watchCountry = watch('country');
  const kidsAge = watch('kidsAge') || [];

  useEffect(() => {
    const targetLength = numberOfDependents || 0;

    let currentKidsAge = [];
    if (personalDetailData?.numberOfDependents) {
      currentKidsAge = personalDetailData.kidsAge || [];
    } else {
      currentKidsAge = getValues('kidsAge') || [];
    }

    const updated = currentKidsAge.slice(0, targetLength);
    while (updated.length < targetLength) {
      updated.push(null);
    }
    setValue('kidsAge', updated);

    if (numberOfDependents === 0) {
      resetField('privateSchool');
      resetField('kidsAge');
    }
  }, [numberOfDependents, setValue, getValues, personalDetailData]);

  useEffect(() => {
    const citizenship = watch('citizenship');
    if (!['australian_pr_visa', 'australian_tr_visa'].includes(citizenship)) {
      resetField('visaSubClass');
      resetField('visaExpiryDate');
    }
  }, [watchCitizenship, resetField]);

  useEffect(() => {
    console.log('watchCurrentResidentalStatus', watchCurrentResidentalStatus);
    if (watchCurrentResidentalStatus === 'renting') {
      resetField('residentalOtherSpecification');
      resetField('isTheResidentialPlaceMortgaged');
    }
    if (watchCurrentResidentalStatus === 'live_own_place') {
      resetField('rentExpense');
      resetField('frequencyOfRent');
      resetField('residentalOtherSpecification');
    }
    if (
      watchCurrentResidentalStatus === 'rent_free_or_boarding_parent' ||
      watchCurrentResidentalStatus === 'rent_free_or_boarding_spouse' ||
      watchCurrentResidentalStatus === 'rent_free_or_boarding_friend' ||
      watchCurrentResidentalStatus === 'rent_free_or_boarding_employer'
    ) {
      console.log('Resetting is the residential place mortgaged');
      resetField('rentExpense');
      resetField('frequencyOfRent');
      resetField('residentalOtherSpecification');
      resetField('isTheResidentialPlaceMortgaged');
    }
    if (watchCurrentResidentalStatus === 'other') {
      resetField('rentExpense');
      resetField('frequencyOfRent');
      resetField('isTheResidentialPlaceMortgaged');
    }
  }, [watchCurrentResidentalStatus, resetField]);

  return (
    <Container width="md">
      <Row>
        <Col xs={24}>
          <Row gutter={30}>
            <Col xs={24}>
              <div
                className="form-section-header form-control"
                id="citizenship"
              >
                <FormMainLabel
                  index={3}
                  label={personalDetailLabelMapper['citizenship']}
                />
                <RadioGroupField
                  name="citizenship"
                  control={control}
                  error={errors.citizenship?.message}
                  options={CITIZENSHIP_OPTIONS}
                  cols={{ sm: 1, md: 1, lg: 2 }}
                  size="large"
                />
              </div>
            </Col>
            {(watchCitizenship === 'australian_pr_visa' ||
              watchCitizenship === 'australian_tr_visa') && (
              <>
                <Col id="visaDetails" xs={24} md={12}>
                  <InputField
                    label="Visa Subclass"
                    name="visaSubClass"
                    control={control}
                    error={errors.visaSubClass?.message ?? ''}
                    placeholder="Enter your visa subclass"
                    size="large"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <DatePickerField
                    label="Visa Expiry Date"
                    name="visaExpiryDate"
                    control={control}
                    error={errors.visaExpiryDate?.message ?? ''}
                    size="large"
                    placeholder="Select Visa Expiry Date"
                    minDate={dayjs()}
                  />
                </Col>
              </>
            )}

            <Col xs={24}>
              <div className="form-section-header form-control">
                <FormMainLabel
                  index={4}
                  label={personalDetailLabelMapper['country']}
                />
                <SelectField
                  showSearch={true}
                  options={countryOptions}
                  name="country"
                  filterName="country"
                  control={control}
                  size="large"
                  placeholder="Select your country"
                  error={errors.country?.message ?? ''}
                />
              </div>
            </Col>
            {watchCountry === 'SG' && (
              <Col xs={24}>
                <FormLabel
                  label="Are you a permanent resident of this country?"
                  size="large"
                />
                <RadioGroupField
                  name="permanentResident"
                  control={control}
                  error={errors.permanentResident?.message}
                  options={AGREE_OPTIONS}
                  cols={{ sm: 1, md: 1, lg: 2 }}
                  size="large"
                />
              </Col>
            )}

            <Col xs={24}>
              <div
                className="form-section-header form-control"
                id="currentResidentalStatus"
              >
                {/* <FormFieldHeading
                      total={total}
                      current={3}
                      title={
                        personalDetailLabelMapper['currentResidentalStatus']
                      }
                      subTitle="We need these to assess your tax situation to give you the best tax outcome."
                    /> */}
                <FormMainLabel
                  index={5}
                  label={personalDetailLabelMapper['currentResidentalStatus']}
                />
                <RadioGroupField
                  name="currentResidentalStatus"
                  control={control}
                  error={errors.currentResidentalStatus?.message}
                  options={RESIDENTIAL_STATUS_OPTIONS}
                  cols={{ sm: 1, md: 1, lg: 2 }}
                  size="large"
                />
              </div>
            </Col>
          </Row>
          <Row gutter={[30, 30]} id="residentalDetails">
            {watchCurrentResidentalStatus === 'renting' && (
              <>
                {/* <Col xs={24} md={8}>
                  <SelectField
                    showSearch={true}
                    options={currencies}
                    name="rentCurrency"
                    control={control}
                    size="large"
                    label="Rent Currency"
                    placeholder="Select your rent currency"
                    error={errors?.rentCurrency?.message ?? ''}
                  />
                </Col> */}
                <Col xs={24} md={12}>
                  <InputField
                    label="Rent Expense (In the currency of your country of residence)"
                    type="number"
                    name="rentExpense"
                    control={control}
                    error={errors.rentExpense?.message ?? ''}
                    placeholder="Enter the rent expense of your country of residence"
                    size="large"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <SelectField
                    label="Frequency of Rent"
                    options={FREQUENCY_OPTIONS}
                    name="frequencyOfRent"
                    filterName="country"
                    control={control}
                    size="large"
                    placeholder="Select your frequency of rent"
                    error={errors.frequencyOfRent?.message ?? ''}
                  />
                </Col>
              </>
            )}

            {watchCurrentResidentalStatus === 'live_own_place' && (
              <Col xs={24}>
                <SelectField
                  options={AGREE_OPTIONS}
                  label="Is This Place Mortgaged?"
                  name="isTheResidentialPlaceMortgaged"
                  control={control}
                  error={errors.isTheResidentialPlaceMortgaged?.message ?? ''}
                  placeholder="Is this place mortgaged?"
                  size="large"
                />
              </Col>
            )}

            {watchCurrentResidentalStatus === 'other' && (
              <Col xs={24}>
                <InputField
                  label="Provide Further Specification"
                  name="residentalOtherSpecification"
                  control={control}
                  error={errors.residentalOtherSpecification?.message ?? ''}
                  placeholder="Please provide further specification of your current residental status"
                  size="large"
                />
              </Col>
            )}
          </Row>
          <Row gutter={[30, 30]} id="maritalStatus">
            <Col xs={24}>
              <div
                className="form-section-header form-control"
                id="maritalStatus"
              >
                <FormMainLabel
                  index={6}
                  label={personalDetailLabelMapper['maritalStatus']}
                />
                <RadioGroupField
                  name="maritalStatus"
                  control={control}
                  error={errors.maritalStatus?.message}
                  options={MARITIAL_STATUS_OPTIONS}
                  cols={{ sm: 1, md: 1, lg: 2 }}
                  size="large"
                />
              </div>
            </Col>
          </Row>
          <Row gutter={[30, 30]} id="maritalDetails">
            <Col xs={24}>
              <div className="form-section-header form-control">
                <FormMainLabel
                  index={7}
                  label={personalDetailLabelMapper['dependentChildren']}
                />
                <RadioGroupField
                  name="numberOfDependents"
                  control={control}
                  error={errors.numberOfDependents?.message}
                  options={DEPENDENT_CHILDREN_OPTIONS}
                  cols={{ sm: 3, md: 4, lg: 6 }}
                  size="large"
                />
              </div>
            </Col>
            {(kidsAge || []).map((_: any, index: number) => (
              <Col xs={24} md={6} lg={4}>
                <InputField
                  label={`Kids Age ${index + 1}`}
                  name={`kidsAge.${index}`}
                  placeholder={`Kid ${index + 1} Age`}
                  type="number"
                  control={control}
                  error={errors?.kidsAge?.[index]?.message ?? ''}
                  size="large"
                />
              </Col>
            ))}
            {numberOfDependents > 0 && (
              <Col xs={24}>
                <SelectField
                  options={PRIVATE_SCHOOL_OPTIONS}
                  label="Do your children go to private school?"
                  placeholder="Do your children go to private school?"
                  name="privateSchool"
                  control={control}
                  error={errors.residentalOtherSpecification?.message ?? ''}
                  size="large"
                />
              </Col>
            )}
          </Row>
          <Col xs={24}>
            <Button
              ref={submitButtonRef}
              type="primary"
              htmlType="submit"
              style={{ display: 'none' }}
            >
              Submit
            </Button>
          </Col>
        </Col>
      </Row>
    </Container>
  );
};

export default NewPersonalDetail;
